import { useEffect, useState } from "react";
import { Banknote, Check, Gift, Loader2, Plus, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

export default function AdminReferrals() {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [pay, setPay] = useState({});   // referrer_id -> importo digitato
  const [busy, setBusy] = useState(null);
  const [lbMonth, setLbMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [lb, setLb] = useState(null);
  const [bonus, setBonus] = useState({});   // referrer_id -> importo bonus
  const [busyBonus, setBusyBonus] = useState(null);
  const [ms, setMs] = useState(null);       // traguardi automatici [{sales, bonus}]
  const [msBusy, setMsBusy] = useState(false);

  const load = () => api.get("/admin/referrals").then(({ data }) => setData(data.data)).catch(() => setData({ error: true }));
  const loadMs = () => api.get("/admin/referrals/milestones").then(({ data }) => setMs(data.data.milestones || [])).catch(() => setMs([]));
  const loadLb = (m) => api.get(`/admin/referrals/leaderboard?month=${m}`).then(({ data }) => setLb(data.data)).catch(() => setLb({ month: m, rows: [] }));
  useEffect(() => { load(); loadMs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLb(null); loadLb(lbMonth); }, [lbMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const eur = (v) => {
    try { return new Intl.NumberFormat(lang, { style: "currency", currency: "EUR" }).format(v || 0); }
    catch { return `€${(v || 0).toFixed(2)}`; }
  };

  if (!data) return <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B35]" /></div>;
  if (data.error) return <p data-testid="refadm-error" className="text-left text-sm text-slate-400 py-10">{t("misc.error")}</p>;

  const payout = async (rid) => {
    const amount = parseFloat(pay[rid]);
    if (!amount || amount <= 0) return;
    setBusy(rid);
    try {
      await api.post("/admin/referrals/payout", { referrer_id: rid, amount });
      toast.success(t("refadm.payoutDone"));
      setPay((p) => ({ ...p, [rid]: "" }));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(null);
    }
  };

  const grantBonus = async (rid) => {
    const amount = parseFloat(bonus[rid]);
    if (!amount || amount <= 0) return;
    setBusyBonus(rid);
    try {
      await api.post("/admin/referrals/bonus", { referrer_id: rid, amount });
      toast.success(t("refadm.bonusDone"));
      setBonus((p) => ({ ...p, [rid]: "" }));
      await Promise.all([load(), loadLb(lbMonth)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusyBonus(null);
    }
  };

  const rankCls = (rank) => rank === 1 ? "bg-amber-100 text-amber-700 border-amber-300"
    : rank === 2 ? "bg-slate-100 text-slate-600 border-slate-300"
    : rank === 3 ? "bg-orange-50 text-orange-600 border-orange-200"
    : "bg-white text-slate-400 border-slate-200";

  const saveMs = async () => {
    setMsBusy(true);
    try {
      const payload = ms.map((m) => ({ sales: parseInt(m.sales) || 0, bonus: parseFloat(m.bonus) || 0 }));
      const { data } = await api.post("/admin/referrals/milestones", { milestones: payload });
      setMs(data.data.milestones);
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setMsBusy(false);
    }
  };

  return (
    <div data-testid="admin-referrals-page" className="mt-10 space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-slate-900">{t("admin.referral")}</h2>
        <p className="text-sm text-slate-500">{t("refadm.sub").replace("{pct}", data.pct).replace("{friend}", data.friend_pct)}</p>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 overflow-x-auto" data-testid="refadm-leaderboard">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#FF6B35]" /> {t("refadm.leaderboard")}
          </h3>
          <input data-testid="refadm-lb-month" type="month" value={lbMonth} onChange={(e) => setLbMonth(e.target.value)}
            className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-mono-t outline-none focus:border-[#FF6B35]" />
        </div>
        {lb === null ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : lb.rows.length === 0 ? (
          <p data-testid="refadm-lb-empty" className="py-8 text-left text-sm text-slate-400">{t("refadm.noMonth")}</p>
        ) : (
          <table className="w-full min-w-[760px] text-sm" data-testid="refadm-lb-table">
            <thead>
              <tr className="text-left font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="pb-2 pr-3">#</th>
                <th className="pb-2 pr-3">{t("refadm.colUser")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.registered")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.monthSales")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.monthEarned")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.milestone")}</th>
                <th className="pb-2 text-right">{t("refadm.bonus")}</th>
              </tr>
            </thead>
            <tbody>
              {lb.rows.map((r) => (
                <tr key={r.referrer_id} data-testid={`refadm-lb-row-${r.referrer_id}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className="py-2.5 pr-3">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full border font-mono-t text-[11px] font-bold ${rankCls(r.rank)}`}>{r.rank}</span>
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="font-medium text-slate-800">{r.name || r.email}</div>
                    <div className="font-mono-t text-[10px] text-slate-400">{r.email} · {r.account_type}</div>
                  </td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{r.registered}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{r.sales}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs font-bold text-emerald-600">{eur(r.earned)}</td>
                  <td className="py-2.5 pr-3 text-right">
                    {r.milestone && (
                      <span data-testid={`refadm-milestone-${r.referrer_id}`} className="rounded-full bg-[#FF6B35]/10 text-[#E8551F] px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                        {r.milestone}+
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 text-right">
                    <span className="inline-flex items-center gap-1.5">
                      <input data-testid={`refadm-bonus-amount-${r.referrer_id}`} type="number" min="0.01" step="0.01" placeholder="0.00"
                        value={bonus[r.referrer_id] || ""} onChange={(e) => setBonus((p) => ({ ...p, [r.referrer_id]: e.target.value }))}
                        className="w-24 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-mono-t outline-none focus:border-[#FF6B35]" />
                      <button data-testid={`refadm-bonus-${r.referrer_id}`} onClick={() => grantBonus(r.referrer_id)} disabled={busyBonus === r.referrer_id}
                        className="rounded-full bg-[#FF6B35] text-white p-2 hover:bg-[#E8551F] transition-colors disabled:opacity-60" title={t("refadm.bonus")}>
                        {busyBonus === r.referrer_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gift className="w-3.5 h-3.5" />}
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6" data-testid="refadm-milestones">
        <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
          <Gift className="w-4 h-4 text-[#FF6B35]" /> {t("refadm.milestones")}
        </h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">{t("refadm.milestonesSub")}</p>
        {ms === null ? (
          <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : (
          <div className="space-y-2.5">
            {ms.map((m, i) => (
              <div key={i} data-testid={`refadm-ms-row-${i}`} className="flex items-center gap-2 flex-wrap">
                <input type="number" min="1" max="100" value={m.sales} aria-label={t("refadm.thresholdSales")}
                  onChange={(e) => setMs((arr) => arr.map((x, j) => j === i ? { ...x, sales: e.target.value } : x))}
                  className="w-24 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-mono-t outline-none focus:border-[#FF6B35]" />
                <span className="font-mono-t text-[10px] uppercase tracking-widest text-slate-400">{t("refadm.thresholdSales")} →</span>
                <input type="number" min="0.5" step="0.5" value={m.bonus} aria-label={t("refadm.bonusEur")}
                  onChange={(e) => setMs((arr) => arr.map((x, j) => j === i ? { ...x, bonus: e.target.value } : x))}
                  className="w-24 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-mono-t outline-none focus:border-[#FF6B35]" />
                <span className="font-mono-t text-[10px] uppercase tracking-widest text-slate-400">{t("refadm.bonusEur")}</span>
                <button data-testid={`refadm-ms-del-${i}`} onClick={() => setMs((arr) => arr.filter((_, j) => j !== i))} disabled={ms.length <= 1}
                  className="rounded-full border border-slate-200 p-1.5 text-slate-400 hover:border-red-400 hover:text-red-500 transition-colors disabled:opacity-40">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2 flex-wrap">
              {ms.length < 5 && (
                <button data-testid="refadm-ms-add" onClick={() => setMs((arr) => [...arr, { sales: Math.max(...arr.map((x) => parseInt(x.sales) || 0)) + 2, bonus: 5 }])}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                  <Plus className="w-3.5 h-3.5" /> {t("refadm.addMilestone")}
                </button>
              )}
              <button data-testid="refadm-ms-save" onClick={saveMs} disabled={msBusy}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1E293B] text-white px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-60">
                {msBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {t("admin.saved")}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 overflow-x-auto" data-testid="refadm-referrers">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{t("refadm.referrers")}</h3>
        {data.referrers.length === 0 ? (
          <p data-testid="refadm-empty" className="py-8 text-left text-sm text-slate-400">{t("refadm.empty")}</p>
        ) : (
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="text-left font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="pb-2 pr-3">{t("refadm.colUser")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.referees")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.commissions")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.wallet")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.payable")}</th>
                <th className="pb-2 pr-3 text-right">{t("refadm.paid")}</th>
                <th className="pb-2 text-right">{t("refadm.pay")}</th>
              </tr>
            </thead>
            <tbody>
              {data.referrers.map((r) => (
                <tr key={r.referrer_id} data-testid={`refadm-row-${r.referrer_id}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className="py-2.5 pr-3">
                    <div className="font-medium text-slate-800">{r.name || r.email}</div>
                    <div className="font-mono-t text-[10px] text-slate-400">{r.email} · {r.account_type}</div>
                  </td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{r.referees}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs font-bold text-emerald-600">{eur(r.commission + r.clawback)}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{eur(r.wallet_credited)}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs font-bold text-amber-600">{eur(r.payable)}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs text-slate-500">{eur(r.paid_out)}</td>
                  <td className="py-2.5 text-right">
                    {r.payable > 0.004 && (
                      <span className="inline-flex items-center gap-1.5">
                        <input data-testid={`refadm-amount-${r.referrer_id}`} type="number" min="0.01" step="0.01" placeholder="0.00"
                          value={pay[r.referrer_id] || ""} onChange={(e) => setPay((p) => ({ ...p, [r.referrer_id]: e.target.value }))}
                          className="w-24 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-mono-t outline-none focus:border-[#FF6B35]" />
                        <button data-testid={`refadm-pay-${r.referrer_id}`} onClick={() => payout(r.referrer_id)} disabled={busy === r.referrer_id}
                          className="rounded-full bg-[#1E293B] text-white p-2 hover:bg-[#FF6B35] transition-colors disabled:opacity-60" title={t("refadm.pay")}>
                          {busy === r.referrer_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Banknote className="w-3.5 h-3.5" />}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 overflow-x-auto" data-testid="refadm-entries">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{t("refadm.entries")}</h3>
        <table className="w-full min-w-[680px] text-sm">
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.entry_id} data-testid={`refadm-entry-${e.entry_id}`} className="border-b border-slate-50 last:border-0">
                <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-500 whitespace-nowrap">
                  {new Date(e.created_at).toLocaleString(lang, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </td>
                <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-600">{e.referrer_id?.slice(0, 14)}…</td>
                <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-400">{e.referee_email || "—"}</td>
                <td className="py-2.5 pr-3">
                  <span className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${e.kind === "commission" ? "bg-emerald-50 text-emerald-700" : e.kind === "clawback" ? "bg-red-50 text-red-600" : e.kind === "bonus" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                    {e.kind}
                  </span>
                </td>
                <td className="py-2.5 pr-3 font-mono-t text-[10px] text-slate-400">{e.status}{e.note ? ` · ${e.note}` : ""}</td>
                <td className={`py-2.5 text-right font-mono-t text-xs font-bold ${e.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {e.amount >= 0 ? "+" : ""}{eur(e.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
